import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FileText } from "lucide-react";
import StudentLayout from "../../layouts/StudentLayout";
import { Arrow, MarkCompleted } from "../../components/Icons";
import {
  CameraOrange,
  CameraWhite,
} from "../../components/student/StudentModuleIcons";
import { Calender2 } from "../../components/student/StudentModuleIcons";
import CourseFeedbackModal from "../../components/student/CourseFeedbackModal";
import CertificatePreviewModal from "../../components/student/CertificatePreviewModal";
import CertificateNameCorrectionModal from "../../components/student/CertificateNameCorrectionModal";
import { useGetEnrolledCourseDataQuery } from "../../services/course";
import { useGetStudentEnrollmentSessionsQuery } from "../../services/courseSession";
import Completed from "../../assets/student/completed-module.svg";
import Total from "../../assets/student/total-modules.svg";
import Time from "../../assets/student/time-spent.svg";
import clockIcon from "../../assets/clock.png";
const formatHours = (minutes) => {
  const totalMinutes = Number(minutes) || 0;
  if (!totalMinutes) return "0 hours";

  const hours = totalMinutes / 60;
  return `${Number.isInteger(hours) ? hours : hours.toFixed(1)} hours`;
};

const formatLectureDuration = (duration) => {
  const totalSeconds = Math.floor(Number(duration) || 0);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const formatTime12h = (time24) => {
  if (!time24 || typeof time24 !== "string") return "";
  const [rawH, rawM] = time24.split(":");
  const hours = Number(rawH);
  const minutes = Number(rawM);
  if (Number.isNaN(hours) || Number.isNaN(minutes)) return time24;
  const period = hours >= 12 ? "PM" : "AM";
  const h12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${h12}:${String(minutes).padStart(2, "0")} ${period}`;
};

const formatSessionDisplayTime = (sessionDate, startTime) => {
  if (!sessionDate) return "";

  const [year, month, day] = sessionDate.split("-").map(Number);
  if (!year || !month || !day) return "";

  const sessionDay = new Date(year, month - 1, day);
  const today = new Date();
  const isToday =
    sessionDay.getFullYear() === today.getFullYear() &&
    sessionDay.getMonth() === today.getMonth() &&
    sessionDay.getDate() === today.getDate();

  const timePart = startTime ? `${formatTime12h(startTime)} PKT` : "";

  if (isToday) {
    return timePart ? `Today – ${timePart}` : "Today";
  }

  const datePart = sessionDay.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });

  return timePart ? `${datePart} - ${timePart}` : datePart;
};

const getStudentName = () => {
  try {
    const user = JSON.parse(localStorage.getItem("loggedInUser") || "{}");
    const name = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();
    return name || "Student";
  } catch {
    return "Student";
  }
};

const ModuleEmptyState = () => (
  <div className="flex flex-col items-center justify-center gap-2 rounded-[18px] border border-dashed border-[#D1D5DB] bg-[#FAFAFA] px-4 py-8 text-center sm:px-6 sm:py-10">
    <FileText className="h-7 w-7 text-[#FF6636]" strokeWidth={1.5} />
    <p className="text-base font-semibold text-[#4B5563]">No lessons yet</p>
    <p className="max-w-md text-sm leading-5 text-[#9CA3AF]">
      Lessons and materials for this module will appear here once added.
    </p>
  </div>
);

const LiveModuleInfo = ({ module }) => (
  <div
    className={`rounded-[18px] px-4 py-5 shadow-[0_0_9.8px_1px_#00000040] sm:px-6 sm:py-6 ${
      module.studentCompleted || module.completed ? "bg-[#D2F9E0]" : "bg-white"
    }`}
  >
    {module.moduleDescription ? (
      <p className="text-base leading-6 text-[#4B5563] sm:text-lg">
        {module.moduleDescription}
      </p>
    ) : null}

    <div
      className={`flex flex-wrap gap-4 text-sm font-medium text-[#1D2026] sm:gap-8 sm:text-base ${
        module.moduleDescription ? "mt-4" : ""
      }`}
    >
      {module.noOfSession ? (
        <span>
          {module.noOfSession}{" "}
          {Number(module.noOfSession) === 1 ? "Session" : "Sessions"}
        </span>
      ) : null}
      {module.sessionDuration ? (
        <span>{module.sessionDuration} mins per session</span>
      ) : null}
    </div>
  </div>
);

const CourseLessonsEmptyState = () => (
  <div className="mt-8 flex flex-col items-center justify-center gap-3 rounded-[18px] border border-dashed border-[#D1D5DB] bg-[#FAFAFA] px-6 py-10 text-center">
    <FileText className="h-8 w-8 text-[#FF6636]" strokeWidth={1.5} />
    <p className="text-base font-semibold text-[#4B5563]">
      No curriculum available yet
    </p>
    <p className="max-w-md text-sm leading-5 text-[#9CA3AF]">
      Class modules and lessons for this course will appear here once added.
    </p>
  </div>
);

const ContinueLearning = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionType, courseId } = useParams();
  const enrollmentId = location.state?.enrollmentId;
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [showNameCorrectionModal, setShowNameCorrectionModal] = useState(false);
  const isRecorded = sessionType === "recorded-session";
  const pageTitle = isRecorded ? "Recorded Session" : "Live Session";
  const {
    data: enrolledCourseResponse,
    isLoading,
    isError,
  } = useGetEnrolledCourseDataQuery(courseId, {
    skip: !courseId,
  });



  const {
    data: enrollmentSessionsResponse,
    isLoading: enrollmentSessionsLoading,
    isError: enrollmentSessionsError,
  } = useGetStudentEnrollmentSessionsQuery(enrollmentId, {
    skip: !enrollmentId || isRecorded,
  });

  const enrollmentSessions = useMemo(() => {
    const data = enrollmentSessionsResponse?.data;
    return Array.isArray(data) ? data : [];
  }, [enrollmentSessionsResponse?.data]);

  const enrolledCourseData = enrolledCourseResponse?.data;
  const course = enrolledCourseData?.course;
  const resolvedEnrollmentId =
    enrollmentId ||
    enrolledCourseData?.enrollmentId ||
    enrolledCourseData?.enrollment?._id;

  const liveSessionCard = useMemo(() => {
    const session = enrollmentSessions[0] || {};

    const timeRange =
      session.startTime && session.endTime
        ? `${session.startTime} to ${session.endTime}`
        : "";

    return {
      displayTime: formatSessionDisplayTime(
        session.sessionDate,
        session.startTime,
      ),
      timeRange,
      meetingLink: session.sessionLink || "",
    };
  }, [enrollmentSessions]);

  const sessionInstructor = useMemo(() => {
    const instructor = enrollmentSessions[0]?.instructor || {};
    const name =
      `${instructor.firstName || ""} ${instructor.lastName || ""}`.trim() ||
      "Instructor";

    return {
      name,
      avatar:
        instructor?.profilePicture?.secureUrl ||
        instructor?.profilePicture ||
        "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=200&q=80",
    };
  }, [enrollmentSessions]);

  const curriculumModules = useMemo(() => {
    const modules = Array.isArray(enrolledCourseData?.modules)
      ? enrolledCourseData.modules
      : [];
    const lectures = Array.isArray(enrolledCourseData?.lectures)
      ? enrolledCourseData.lectures
      : [];
    const pdfMaterials = Array.isArray(enrolledCourseData?.pdfMaterials)
      ? enrolledCourseData.pdfMaterials
      : [];

    return modules
      .map((module) => {
        const normalized = {
          ...module,
          ...(module?._doc || {}),
        };

        return {
          ...normalized,
          lectures: lectures.filter(
            (lecture) => lecture.moduleItBelongTo === normalized._id,
          ),
          pdfMaterials: pdfMaterials.filter(
            (pdf) => pdf.moduleItBelongTo === normalized._id,
          ),
        };
      })
      .sort((a, b) => (a.moduleIndex ?? 0) - (b.moduleIndex ?? 0));
  }, [
    enrolledCourseData?.modules,
    enrolledCourseData?.lectures,
    enrolledCourseData?.pdfMaterials,
  ]);

  const nextLectureId = useMemo(() => {
    for (const module of curriculumModules) {
      if (
        !(module.studentCompleted || module.completed) &&
        Array.isArray(module.lectures) &&
        module.lectures.length
      ) {
        return module.lectures[0]._id;
      }
    }
    return null;
  }, [curriculumModules]);

  const getLectureBackground = (module, lecture) => {
    if (module.studentCompleted || module.completed) return "bg-[#D2F9E0]";
    if (lecture._id === nextLectureId) return "bg-[#089BC433]";
    return "bg-white";
  };

  const getPdfBackground = (module) => {
    if (module.studentCompleted || module.completed) return "bg-[#D2F9E0]";
    return "bg-white";
  };

  const handleOpenPdf = (url) => {
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleOpenLecture = (lecture) => {
    const videoUrl = lecture?.lectureInfo?.secureUrl;
    if (!videoUrl || !lecture?._id) return;

    navigate(`/student/lectures/${lecture._id}`, {
      state: {
        title: lecture.title,
        videoUrl,
      },
    });
  };

  const totalModules = curriculumModules.length;
  const completedModules = curriculumModules.filter(
    (module) => module.studentCompleted || module.completed,
  ).length;
  const progressPercentage =
    totalModules > 0 ? Math.round((completedModules / totalModules) * 100) : 0;
  const totalMinutes = curriculumModules.reduce(
    (sum, module) => sum + (Number(module.sessionDuration) || 0),
    0,
  );
  const completedMinutes = curriculumModules
    .filter((module) => module.studentCompleted || module.completed)
    .reduce((sum, module) => sum + (Number(module.sessionDuration) || 0), 0);
  const remainingMinutes = Math.max(totalMinutes - completedMinutes, 0);

  const statCards = [
    {
      title: "Total Modules",
      value: totalModules,
      bg: "bg-[#923D8C4D]",
      icon: Total,
    },
    {
      title: "Completed Module",
      value: completedModules,
      bg: "bg-[#23BD334D]",
      icon: Completed,
    },
    {
      title: "Time spent",
      value: formatHours(completedMinutes),
      bg: "bg-[#089BC433]",
      icon: Time,
    },
    {
      title: "Estimated remaining",
      value: formatHours(remainingMinutes),
      bg: "bg-[#FF663633]",
      icon: Time,
    },
  ];

  return (
    <StudentLayout>
      <section className="px-4 sm:px-5 pb-20 sm:pb-40 pt-10 sm:pt-16 lg:px-12 2xl:pl-[67px] 2xl:pr-[79px] font-['Roboto'] overflow-x-hidden">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-4 sm:gap-8 text-black"
          aria-label="Go back"
        >
          <Arrow />
          <h2 className="text-xl sm:text-2xl font-semibold tracking-[-1%] text-[#1D2026]">
            Back to Dashboard
          </h2>
        </button>

        {isLoading ? (
          <div className="mt-10 text-base text-[#6B7280]">
            Loading course data...
          </div>
        ) : isError || !course ? (
          <div className="mt-10 text-base text-[#6B7280]">
            Could not load this course right now.
          </div>
        ) : (
          <div className="mt-10 sm:mt-20">
            <div className="relative mx-auto w-full max-w-[1564px]">
              <div className="relative aspect-[16/10] sm:aspect-[16/9] w-full overflow-hidden lg:aspect-auto lg:h-[490px] lg:overflow-visible">
                <img
                  src={course.courseThumbnail?.secureUrl}
                  alt={course.courseTitle}
                  className="absolute inset-0 z-10 h-full w-full object-cover lg:left-0 lg:top-0 lg:h-[486px] lg:max-w-[1572px]"
                />
                <div className="pointer-events-none absolute inset-0 border-2 sm:border-[3px] border-[#923D8C] shadow-[0_0_4px_0px_#00000040] lg:inset-auto lg:bottom-0 lg:-right-6 lg:-top-6 lg:h-[490px] lg:w-full lg:max-w-[1545px]" />
              </div>
            </div>

            <div className="mt-8 sm:mt-12">
              <h1 className="text-[26px] sm:text-[32px] lg:text-[40px] leading-tight sm:leading-[48px] lg:leading-[56px] tracking-[-1%] font-bold text-black break-words">
                {course.courseTitle || pageTitle}
              </h1>
            </div>

            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between text-sm font-medium text-[#1D2026]">
                <span>Progress</span>
                <span>{progressPercentage}%</span>
              </div>
              <div className="h-[12px] sm:h-[15px] w-full rounded-full bg-[#D9D9D9]">
                <div
                  className="h-full rounded-full bg-[#FF6636]"
                  style={{ width: `${progressPercentage}%` }}
                />
              </div>
            </div>

            {isRecorded ? (
              <div className="mb-16 sm:mb-28 mt-10 sm:mt-[74px] grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-8 xl:grid-cols-4 max-w-[1422px]">
                {statCards.map((card) => (
                  <div
                    key={card.title}
                    className={`flex items-center gap-4 sm:gap-6 p-4 sm:p-6 shadow-[0_0_7.3px_0_#00000040] ${card.bg}`}
                  >
                    <div className="flex h-12 w-12 sm:h-[60px] sm:w-[60px] shrink-0 items-center justify-center bg-[#F5F5F5]">
                      <img
                        src={card.icon}
                        alt=""
                        className="max-h-8 max-w-8 sm:max-h-none sm:max-w-none"
                      />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm sm:text-base text-[#4E5566]">
                        {card.title}
                      </p>
                      <p className="mt-1 text-lg sm:text-xl font-medium text-[#1D2026]">
                        {card.value}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-8 flex flex-col items-center gap-5 rounded-[18px] bg-white px-5 py-6 shadow-[0_0_9.8px_1px_#00000040] sm:flex-row sm:gap-7 sm:px-8 sm:py-7 lg:px-12">
                <img
                  src={sessionInstructor.avatar}
                  alt={sessionInstructor.name}
                  className="h-28 w-28 shrink-0 rounded-full object-cover sm:h-[140px] sm:w-[140px] lg:h-[182px] lg:w-[182px]"
                />
                <div className="text-center sm:text-left">
                  <p className="text-xl sm:text-2xl font-semibold text-[#1D2026]">
                    {enrollmentSessionsLoading
                      ? "Loading instructor..."
                      : sessionInstructor.name}
                  </p>
                  <p className="mt-2 sm:mt-5 text-sm sm:text-base text-black">
                    Course Instructor
                  </p>
                </div>
              </div>
            )}

            {!isRecorded ? (
              <article className="my-8 rounded-[18px] bg-[#F5F5F5] px-5 py-8 shadow-[0_0_9.8px_1px_#00000040] sm:px-8 sm:py-14 lg:px-10 lg:pb-28 xl:pl-32 xl:pr-20">
                <div className="flex flex-col gap-8 lg:flex-row lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-4 sm:gap-9">
                      <div className="shrink-0">
                        <CameraOrange />
                      </div>
                      <h3 className="text-2xl sm:text-[28px] lg:text-[32px] font-medium leading-tight sm:leading-[56px] tracking-[-1%] text-black">
                        Live Sessions
                      </h3>
                    </div>

                    <div className="mt-5 flex items-start gap-4 sm:mt-7 sm:items-center sm:gap-9">
                      <div className="mt-1 shrink-0 sm:mt-0">
                        <Calender2 />
                      </div>
                      <p className="break-words text-lg font-medium leading-snug tracking-[-1%] text-black sm:text-2xl sm:leading-[40px] lg:text-[32px] lg:leading-[56px]">
                        {enrollmentSessionsLoading
                          ? "Loading session..."
                          : liveSessionCard.displayTime}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => {
                        if (!liveSessionCard.meetingLink) return;
                        window.open(
                          liveSessionCard.meetingLink,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      }}
                      disabled={!liveSessionCard.meetingLink}
                      className="mt-8 inline-flex w-full max-w-[430px] items-center justify-center gap-4 bg-[#FF6636] px-6 py-4 text-lg font-bold leading-6 text-white transition-colors hover:bg-[#e65a2e] disabled:cursor-not-allowed disabled:opacity-60 sm:mt-14 sm:gap-7 sm:px-12 sm:py-[23.5px] sm:text-2xl"
                    >
                      <CameraWhite />
                      Join Live Class
                    </button>
                  </div>

                  <div className="space-y-6 sm:space-y-8">
                    <div className="flex items-center gap-4 sm:gap-8">
                      <svg
                        width="28"
                        height="28"
                        viewBox="0 0 28 28"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        xlink="http://www.w3.org/1999/xlink"
                        className="shrink-0"
                      >
                        <rect
                          width="28"
                          height="28"
                          fill="url(#pattern0_2934_8566)"
                        />
                        <defs>
                          <pattern
                            id="pattern0_2934_8566"
                            patternContentUnits="objectBoundingBox"
                            width="1"
                            height="1"
                          >
                            <use
                              href="#image0_2934_8566"
                              transform="scale(0.0078125)"
                            />
                          </pattern>
                          <image
                            id="image0_2934_8566"
                            width="128"
                            height="128"
                            preserveAspectRatio="none"
                            href="data:image/png;base64,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"
                          />
                        </defs>
                      </svg>

                      <p className="break-words text-sm sm:text-base text-black">
                        {liveSessionCard.timeRange}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            ) : null}

            <div>
              <h1 className="text-2xl font-bold leading-tight tracking-[-1%] text-black sm:text-3xl sm:leading-[56px] lg:text-4xl">
                Course Lessons
              </h1>

              {curriculumModules.length === 0 ? (
                <CourseLessonsEmptyState />
              ) : (
                <div className="mt-4 space-y-7">
                  {curriculumModules.map((module, index) => {
                    const moduleLectures = module.lectures || [];
                    const moduleMaterials = module.pdfMaterials || [];
                    const hasModuleContent =
                      moduleLectures.length > 0 || moduleMaterials.length > 0;

                    return (
                      <div key={module._id}>
                        <h2 className="text-lg font-bold leading-snug text-black sm:text-xl">
                          Module {index + 1}: {module.moduleName}
                        </h2>

                        <div className="mt-5 space-y-4 sm:mt-8 sm:space-y-7">
                          {!hasModuleContent ? (
                            isRecorded ? (
                              <ModuleEmptyState />
                            ) : (
                              <LiveModuleInfo module={module} />
                            )
                          ) : (
                            <>
                              {moduleLectures.map((lecture, idx) => (
                                <button
                                  key={lecture._id}
                                  type="button"
                                  onClick={() => handleOpenLecture(lecture)}
                                  disabled={!lecture?.lectureInfo?.secureUrl}
                                  className={`flex w-full flex-col gap-3 rounded-[18px] px-4 py-4 text-left shadow-[0_0_9.8px_1px_#00000040] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-row sm:items-center sm:justify-between sm:px-6 ${getLectureBackground(module, lecture)}`}
                                >
                                  <div className="flex min-w-0 items-center gap-4 sm:gap-7">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] bg-white shadow-[0_0_6.6px_-1px_#00000040] sm:h-[74px] sm:w-[74px]">
                                      <p>
                                        {module.studentCompleted ||
                                        module.completed ? (
                                          <MarkCompleted />
                                        ) : (
                                          idx + 1
                                        )}
                                      </p>
                                    </div>
                                    <p className="min-w-0 break-words text-base font-bold text-black sm:text-xl">
                                      {lecture.title}
                                    </p>
                                  </div>
                                  <span className="inline-flex shrink-0 items-center gap-2 pl-16 text-base text-black leading-[23px] sm:gap-3 sm:pl-0 sm:text-xl">
                                    <img
                                      src={clockIcon}
                                      alt=""
                                      className="size-5"
                                    />
                                    {formatLectureDuration(lecture.duration)}
                                  </span>
                                </button>
                              ))}

                              {moduleMaterials.map((pdf) => (
                                <button
                                  key={pdf._id}
                                  type="button"
                                  onClick={() =>
                                    handleOpenPdf(
                                      pdf.pdfMaterialInfo?.secureUrl,
                                    )
                                  }
                                  className={`flex w-full flex-col gap-3 rounded-[18px] px-4 py-4 text-left shadow-[0_0_9.8px_1px_#00000040] transition-opacity hover:opacity-90 sm:flex-row sm:items-center sm:justify-between sm:px-6 ${getPdfBackground(module)}`}
                                >
                                  <div className="flex min-w-0 items-center gap-4 sm:gap-7">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[18px] bg-white shadow-[0_0_6.6px_-1px_#00000040] sm:h-[74px] sm:w-[74px]">
                                      <FileText
                                        size={28}
                                        className="text-[#EF4444]"
                                        strokeWidth={1.75}
                                      />
                                    </div>
                                    <p className="min-w-0 break-words text-base font-bold text-black sm:text-xl">
                                      {pdf.title}
                                    </p>
                                  </div>
                                  <span className="pl-16 text-sm font-medium text-[#6B7280] sm:pl-0 sm:text-base">
                                    PDF
                                  </span>
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="mt-10 sm:mt-14">
              <h1 className="text-2xl font-bold leading-tight tracking-[-1%] text-black sm:text-3xl sm:leading-[56px] lg:text-4xl">
                Course Resources
              </h1>

              <div className="mt-6 space-y-6 sm:space-y-14">
                <button
                  type="button"
                  onClick={() => setShowFeedbackModal(true)}
                  className="flex w-full flex-col gap-4 rounded-[9px] bg-[#E9FCF0] px-5 py-6 transition-opacity hover:opacity-90 sm:flex-row sm:gap-9 sm:px-10 sm:py-10 lg:px-[72px] lg:py-[51px]"
                >
                  <div className="shrink-0">
                    <svg
                      width="53"
                      height="53"
                      viewBox="0 0 53 53"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      xlink="http://www.w3.org/1999/xlink"
                      className="h-10 w-10 sm:h-[53px] sm:w-[53px]"
                    >
                      <rect
                        width="53"
                        height="53"
                        fill="url(#pattern0_2968_7694)"
                      />
                      <defs>
                        <pattern
                          id="pattern0_2968_7694"
                          patternContentUnits="objectBoundingBox"
                          width="1"
                          height="1"
                        >
                          <use
                            href="#image0_2968_7694"
                            transform="scale(0.0078125)"
                          />
                        </pattern>
                        <image
                          id="image0_2968_7694"
                          width="128"
                          height="128"
                          preserveAspectRatio="none"
                          href="data:image/png;base64,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"
                        />
                      </defs>
                    </svg>
                  </div>
                  <div className="min-w-0 text-left">
                    <p className="text-lg font-bold leading-[23px] text-black sm:text-xl">
                      Course Feedback
                    </p>
                    <p className="mt-2 text-base leading-[23px] text-[#938A96] sm:mt-3 sm:text-xl">
                      Share your Thoughts
                    </p>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setShowCertificateModal(true)}
                  disabled={!resolvedEnrollmentId}
                  className="flex w-full items-center justify-center bg-[#FF663633] px-4 py-5 text-lg font-medium leading-7 text-black transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 sm:px-8 sm:py-6 sm:text-2xl"
                >
                  Preview Certificate
                </button>
              </div>
            </div>
          </div>
        )}
      </section>

      <CourseFeedbackModal
        open={showFeedbackModal}
        onClose={() => setShowFeedbackModal(false)}
        courseId={courseId}
      />

      <CertificatePreviewModal
        open={showCertificateModal}
        onClose={() => setShowCertificateModal(false)}
        onRequestNameCorrection={() => setShowNameCorrectionModal(true)}
        enrollmentId={resolvedEnrollmentId}
      />

      <CertificateNameCorrectionModal
        open={showNameCorrectionModal}
        onClose={() => setShowNameCorrectionModal(false)}
        currentName={getStudentName()}
      />
    </StudentLayout>
  );
};

export default ContinueLearning;
